/**
 * Export Feature Module
 * Central exports for all export functionality
 */

// Main Export Manager
export { ExportManager, createExportManager } from './ExportManager';

// Export Types
export type {
    ExportFormat,
    ExportQuality,
    ExportConfig,
    ExportResult,
    ExportMetadata,
    PNGExportOptions,
    JPEGExportOptions,
    SVGExportOptions,
    GIFExportOptions,
    JSONExportOptions,
    ExportProgressCallback,
    ExportHandler,
} from './exportTypes';

// Export Handlers
export { PNGExportHandler, createPNGExportHandler } from './exportFormats/pngExport';
export { JPEGExportHandler, createJPEGExportHandler } from './exportFormats/jpegExport';
export { SVGExportHandler, createSVGExportHandler } from './exportFormats/svgExport';
export { GIFExportHandler, createGIFExportHandler } from './exportFormats/gifExport';
export {
    JSONExportHandler,
    createJSONExportHandler,
    importFromJSON,
    type JSONExportData,
} from './exportFormats/jsonExport';

// Export Utilities
export {
    generateFilename,
    getFileExtension,
    getMimeType,
    downloadBlob,
    downloadString,
    createCanvasFromElement,
    canvasToBlob,
    getElementBounds,
    createExportMetadata,
    validateExportConfig,
    createDefaultConfig,
    sanitizeFilename,
    calculateExportDimensions,
} from './exportUtils';

// React Hook
export { useExport, type UseExportOptions } from './useExport';

/**
 * Quick Start Export Functions
 * Convenience functions for common export operations
 */
import { createExportManager } from './ExportManager';
import { Model } from '../../types';

const exportManager = createExportManager();

/**
 * Quick export to PNG
 */
export const quickExportPNG = async (
    element: HTMLElement,
    model: Model,
    options?: {
        quality?: 1 | 2 | 4;
        transparent?: boolean;
        filename?: string;
    }
) => {
    return exportManager.exportToPNG(
        element,
        model,
        options?.quality,
        options?.transparent
    );
};

/**
 * Quick export to JPEG
 */
export const quickExportJPEG = async (
    element: HTMLElement,
    model: Model,
    options?: {
        quality?: 1 | 2 | 4;
        backgroundColor?: string;
        filename?: string;
    }
) => {
    return exportManager.exportToJPEG(
        element,
        model,
        options?.quality,
        options?.backgroundColor
    );
};

/**
 * Quick export to SVG
 */
export const quickExportSVG = async (
    element: HTMLElement,
    model: Model,
    options?: {
        transparent?: boolean;
        filename?: string;
    }
) => {
    return exportManager.exportToSVG(element, model, options?.transparent);
};

/**
 * Quick export to JSON
 */
export const quickExportJSON = async (
    element: HTMLElement,
    model: Model,
    // options?: {
    //     filename?: string;
    // }
) => {
    return exportManager.exportToJSON(element, model);
};

/** Quick export to an infinitely looping animated GIF. */
export const quickExportGIF = async (
    element: HTMLElement,
    model: Model,
    options?: { durationSeconds?: number; fps?: number }
) => {
    return exportManager.exportToGIF(
        element,
        model,
        options?.durationSeconds,
        options?.fps
    );
};
